/**
 * Changelog — public page rendering the "Recently Shipped" section of PRD.md
 * as a vertical timeline.
 *
 * Pulls /api/public/changelog (no auth), groups by date, shows version
 * pill + headline + first paragraph for each entry.
 *
 * Strategic value: trust signal for HN visitors. Live changelog says
 * "this team ships," and being PRD-sourced means there's exactly one
 * source of truth — no separate marketing copy to maintain.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  GitCommit, Calendar, Loader2, ArrowRight,
  Sparkles, ShieldCheck, Wrench, Gauge, Rocket, Zap, FlaskConical, BookOpen, Package,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import BackToHomePill from '../components/BackToHomePill';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  feature: { label: 'Feature', icon: Sparkles, color: 'emerald' },
  security: { label: 'Security', icon: ShieldCheck, color: 'cyan' },
  performance: { label: 'Performance', icon: Gauge, color: 'amber' },
  fix: { label: 'Fix', icon: Wrench, color: 'rose' },
  release: { label: 'Release', icon: Package, color: 'orange' },
  improvement: { label: 'Improvement', icon: Zap, color: 'sky' },
  experiment: { label: 'Experiment', icon: FlaskConical, color: 'violet' },
  docs: { label: 'Docs', icon: BookOpen, color: 'teal' },
  ship: { label: 'Shipped', icon: Rocket, color: 'fuchsia' },
};

const CategoryPill = ({ category, idx }) => {
  const meta = CATEGORY_META[category];
  if (!meta) return null;
  const Icon = meta.icon;
  const colorClass = {
    emerald: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
    cyan: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/30',
    amber: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
    rose: 'bg-rose-500/10 text-rose-300 border-rose-500/30',
    orange: 'bg-orange-500/10 text-orange-300 border-orange-500/30',
    sky: 'bg-sky-500/10 text-sky-300 border-sky-500/30',
    violet: 'bg-violet-500/10 text-violet-300 border-violet-500/30',
    teal: 'bg-teal-500/10 text-teal-300 border-teal-500/30',
    fuchsia: 'bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-500/30',
  }[meta.color] || 'bg-slate-700/40 text-slate-300 border-white/10';

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider border ${colorClass}`}
      data-testid={`changelog-category-${category}-${idx}`}
    >
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
};

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'release', label: 'Releases' },
  { id: 'feature', label: 'Features' },
  { id: 'security', label: 'Security' },
  { id: 'fix', label: 'Fixes' },
  { id: 'improvement', label: 'Polish' },
];

export default function Changelog() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    document.title = 'Changelog · CARTIEAI';
    (async () => {
      try {
        const res = await fetch(`${API}/api/public/changelog?limit=80`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        setEntries(json.entries || []);
      } catch (e) {
        setErr(String(e.message || e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredEntries = filter === 'all'
    ? entries
    : entries.filter((e) => (e.categories || []).includes(filter));

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100 -mt-px">
      <SEOHead
        title="Changelog — Every CARTIEAI release, dated and sourced from the PRD"
        description="Live changelog of CARTIEAI features, fixes, and polish, sourced directly from our PRD.md. Updated every release."
        canonical="/changelog"
        ogSlug="changelog"
      />
      <BackToHomePill />

      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/10">
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-emerald-200/40 blur-[120px] -z-10" />
        <div className="absolute -top-20 left-1/4 w-72 h-72 bg-emerald-200/40 blur-[120px] -z-10" />
        <div className="absolute top-40 -left-32 w-80 h-80 bg-teal-200/30 blur-[120px] -z-10" />

        <div className="max-w-4xl mx-auto px-6 pt-16 pb-10">
          <Badge className="bg-slate-900 text-emerald-300 border-0 font-mono uppercase tracking-[0.18em] mb-5 shadow-lg" data-testid="changelog-badge">
            <GitCommit className="w-3 h-3 mr-1.5" /> Live · Sourced from CHANGELOG.md
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-5 text-white leading-[1.05]" data-testid="changelog-h1">
            Every release,<br />
            <span className="text-emerald-400">dated and documented.</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl leading-relaxed font-medium">
            No marketing fluff. Every entry below is sourced from our internal changelog — the same one we ship against. <span className="text-white">If it shipped, it&apos;s here.</span>
          </p>

          {/* Filter pills */}
          <div className="flex flex-wrap gap-2 mt-6" data-testid="changelog-filters">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider transition-all ${
                  filter === f.id
                    ? 'bg-emerald-500 text-slate-950 font-bold shadow-lg shadow-emerald-500/30'
                    : 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-emerald-300 border border-white/10'
                }`}
                data-testid={`changelog-filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {loading && (
          <div className="flex items-center gap-3 text-slate-500 py-12" data-testid="changelog-loading">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading…
          </div>
        )}
        {err && (
          <Card className="bg-rose-500/10 border-rose-500/30" data-testid="changelog-error">
            <CardContent className="p-4 text-sm text-rose-300">Changelog unavailable — {err}</CardContent>
          </Card>
        )}

        {!loading && !err && filteredEntries.length === 0 && (
          <div className="text-slate-500 italic" data-testid="changelog-empty">
            No entries match this filter — try &ldquo;All&rdquo;.
          </div>
        )}

        <div className="relative" data-testid="changelog-timeline">
          {/* Vertical rail */}
          {filteredEntries.length > 0 && (
            <div className="absolute left-[15px] top-2 bottom-2 w-px bg-emerald-300 bg-gradient-to-b from-emerald-300 via-emerald-200 to-teal-200" />
          )}

          <div className="space-y-10">
            {filteredEntries.map((e, idx) => (
              <div key={idx} className="relative pl-12" data-testid={`changelog-entry-${idx}`}>
                {/* Marker */}
                <div className="absolute left-0 top-1.5 w-8 h-8 rounded-full bg-slate-900/60 border-2 border-emerald-400 flex items-center justify-center shadow-md">
                  <Calendar className="w-3.5 h-3.5 text-emerald-400" />
                </div>

                {/* Card */}
                <div>
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    {e.version && (
                      <code className="text-[11px] font-mono px-2 py-0.5 rounded bg-slate-900/70 border border-white/10 text-emerald-300" data-testid={`changelog-version-${idx}`}>
                        {e.version}
                      </code>
                    )}
                    <span className="text-xs text-slate-500 font-mono uppercase tracking-widest" data-testid={`changelog-date-${idx}`}>
                      {e.date}
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {(e.categories || []).map((cat) => (
                        <CategoryPill key={cat} category={cat} idx={idx} />
                      ))}
                    </div>
                  </div>
                  <h2 className="text-lg sm:text-xl font-bold text-white leading-snug mb-1" data-testid={`changelog-title-${idx}`}>
                    {e.title}
                  </h2>
                  {e.headline && (
                    <div className="text-sm text-emerald-300 font-semibold mb-2 tabular-nums" data-testid={`changelog-headline-${idx}`}>
                      {e.headline}
                    </div>
                  )}
                  <p className="text-sm text-slate-400 leading-relaxed" data-testid={`changelog-summary-${idx}`}>
                    {e.summary}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 p-6 rounded-2xl bg-slate-900 bg-gradient-to-r from-slate-900 via-slate-800 to-emerald-900 border border-emerald-500/30 shadow-xl shadow-emerald-500/10 text-white">
          <h2 className="text-2xl font-bold mb-2">Want to be in the next release?</h2>
          <p className="text-slate-200 mb-4 max-w-2xl">
            Pilots that ask for features early ship them in the next 7 days. <span className="text-emerald-300 font-semibold">14-day pilot, no credit card, 3× ROI pledge or refund.</span>
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/login">
              <Button data-testid="changelog-cta-signup" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold shadow-lg shadow-emerald-500/30">
                Start free pilot <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/policy-playground">
              <Button variant="outline" data-testid="changelog-cta-playground" className="border-white/30 bg-white/10 text-white hover:bg-white/20">
                Try the Rego playground
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
